import { IconButton, SxProps, useTheme } from "@mui/material";
import { QUERY_PROGRESS_NOT_STARTED, TRACK_EVENTS } from "core/consts";
import { id } from "date-fns/locale";
import RSButton from "ds_legacy/components/RSButton";
import Tooltip from "ds_legacy/components/Tooltip";
import { dp } from "ds_legacy/materials/metrics";
import { FONT_SIZE_14, FONT_SIZE_16 } from "ds_legacy/materials/typography";
import { CopyIcon } from "lucide-react";
import { ReactElement } from "react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import { usePrint } from "../Contexts";
import { useMedia } from "../ResponsiveMedia";
import { useCopy } from "./useCopy";

function CopyTooltipWrapper({
  children,
  copyMessage,
  copyStatus,
}: {
  children: ReactElement<any>;
  copyMessage: string;
  copyStatus: string;
}) {
  const { isDesktop } = useMedia();

  if (isDesktop) {
    return (
      <Tooltip isHoverable title={copyMessage}>
        {children}
      </Tooltip>
    );
  }

  return (
    <Tooltip
      open={copyStatus !== QUERY_PROGRESS_NOT_STARTED}
      title={copyMessage}
    >
      {children}
    </Tooltip>
  );
}

export function EmailCopyButton({
  sx,
  title,
  value,
}: {
  sx?: SxProps;
  title: string;
  value: string;
}) {
  const translations = useTranslations();
  const { trackEvent } = useTracking();
  const theme = useTheme();
  const isPrint = usePrint();

  const { copyMessage, copyStatus, copyToClipboard } = useCopy({
    onCopyMessage:
      translations.providersearch.providerDetailsPage.contactSection
        .copyEmailTooltip,
  });

  if (isPrint) return null;

  return (
    <CopyTooltipWrapper copyMessage={copyMessage} copyStatus={copyStatus}>
      <IconButton
        aria-label={title}
        data-testid={id}
        sx={sx}
        onClick={() => {
          trackEvent({ name: TRACK_EVENTS.COPY_BUTTON });
          copyToClipboard(value);
        }}
      >
        <CopyIcon
          size={FONT_SIZE_14}
          style={{
            color: theme.palette.grey[600],
            fontSize: FONT_SIZE_14,
          }}
        />
      </IconButton>
    </CopyTooltipWrapper>
  );
}

export function ApprovalLinkCopyButton({
  href,
  label,
}: {
  href: string;
  label: string;
}) {
  const translations = useTranslations();
  const theme = useTheme();

  const { copyMessage, copyToClipboard } = useCopy({
    copySuccessMessage:
      translations.insuranceApp.eligibilityTab.approvalFlow.tooltipLinkCopied,
    onCopyMessage:
      translations.insuranceApp.eligibilityTab.approvalFlow.buttonCopyLink,
  });

  return (
    <RSButton
      aria-label={label}
      ariaProps={{ "data-approval-link": href }}
      buttonWrapperStyle={{ maxHeight: dp(26) }}
      color="primary"
      id="copy-approval-link"
      loading="na"
      size="small"
      tooltip={copyMessage}
      variant="text"
      onClick={() => {
        copyToClipboard(href);
      }}
    >
      <span className="whitespace-nowrap">{label}</span>
      <span>
        <CopyIcon
          size={FONT_SIZE_16}
          style={{
            color: theme.palette.primary.main,
            fontSize: FONT_SIZE_16,
            marginLeft: dp(4),
          }}
        />
      </span>
    </RSButton>
  );
}
